<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  defineComponent,
  type Component,
} from "vue";
import type { UseHeadInput } from "@unhead/vue";
import type { SectionsInterface } from "~~/server/types/SectionsInterface";
import { parseSectionContent, renderSectionContent } from "~/utils/sectionContent";

const Banner1SectionComponent = defineAsyncComponent(() => import("~/components/Sections/banner1SectionComponent.vue"),);
const Banner2SectionComponent = defineAsyncComponent(() => import("~/components/Sections/banner2SectionComponent.vue"),);
const Banner3SectionComponent = defineAsyncComponent(() => import("~/components/Sections/banner3SectionComponent.vue"),);
const ProductsGridSectionComponent = defineAsyncComponent(() => import("~/components/Sections/ProductsGridSectionComponent.vue"),);
const BrandsSectionComponent = defineAsyncComponent(() => import("~/components/Sections/BrandsSectionComponent.vue"));
const NewsGridSectionComponent = defineAsyncComponent(() => import("~/components/Sections/NewsGridSectionComponent.vue"),);
const YouTubeVideoSectionComponent = defineAsyncComponent(() => import("~/components/Sections/YouTubeVideoSectionComponent.vue"),);

const props = defineProps<{
  page?: string;
  url?: string;
  section?: SectionsInterface;
}>();

const componentRegistry: Record<string, Component> = {
  "products-grid": ProductsGridSectionComponent,
  "brands-list": BrandsSectionComponent,
  "news-grid": NewsGridSectionComponent,
  banner1: Banner1SectionComponent,
  banner2: Banner2SectionComponent,
  banner3: Banner3SectionComponent,
  "youtube-video": YouTubeVideoSectionComponent,
};

const styleId = `backend-section-${useId()}`;
const content = computed(() => parseSectionContent(props.section?.html || "", Object.keys(componentRegistry)));
const SectionContent = defineComponent({
  setup: () => () => renderSectionContent(content.value, componentRegistry),
});

const links = computed<UseHeadInput["link"]>(() => {
  const images = props.section?.images ?? { web: null, mobile: null };
  const mobile = images.mobile;
  const desktop = images.web;

  const out: NonNullable<UseHeadInput["link"]> = [];

  if (mobile) {
    out.push({
      rel: "preload",
      as: "image",
      href: mobile,
      media: "(max-width: 992px)",
      fetchpriority: "high",
    });
  }

  if (desktop) {
    out.push({
      rel: "preload",
      as: "image",
      href: desktop,
      media: "(min-width: 993px)",
      fetchpriority: "high",
    });
  }

  return out;
});

useHead(() => ({
  link: links.value,
  style: props.section?.css
    ? [{ key: styleId, id: styleId, textContent: props.section.css }]
    : [],
}));
</script>

<template>
  <div><SectionContent /></div>
</template>
